// Mapeo de perfiles con sus IDs según el orden
const PROFILE_MAP = {
  Desarrollo: { id: 1, name: 'Desarrollo' },
  Equilibrado: { id: 2, name: 'Equilibrado' },
  Empatico: { id: 3, name: 'Empatico' },
  Colaborativo: { id: 4, name: 'Colaborativo' },
  Autoconsciente: { id: 5, name: 'Autoconsciente' },
  Autorregulado: { id: 6, name: 'Autorregulado' },
  Motivado: { id: 7, name: 'Motivado' },
  Estrategico: { id: 8, name: 'Estrategico' },
  'Lider inspiracional': { id: 9, name: 'Lider inspiracional' },
  'Sin clasificar': { id: 0, name: 'Sin clasificar' },
};

const LEVELS = [5, 9, 13, 18, 22];

/**
 * Función para normalizar valores usando la misma lógica que obtainResults
 * de EmotionalIntelligenceResults
 * @param {number} value - Valor original
 * @returns {number} - Valor normalizado (0-5 barras)
 */
const normalizeValue = (value) => {
  // Si el valor está en el rango esperado (1-5), no normalizar
  if (value >= 1 && value < 5) {
    return value;
  }

  // Usar la misma lógica que obtainResults en EmotionalIntelligenceResults
  if (value >= LEVELS[4]) return 5; // very-high: >= 22 -> 5 barras
  if (value >= LEVELS[3]) return 4; // high: >= 18 -> 4 barras
  if (value >= LEVELS[2]) return 3; // half: >= 13 -> 3 barras
  if (value >= LEVELS[1]) return 2; // low: >= 9 -> 2 barras
  if (value >= LEVELS[0]) return 1; // very-low: >= 5 -> 1 barra
  return 0; // none: < 5 -> 0 barras
};

/**
 * Función para determinar el perfil emocional basado en las respuestas
 * @param {string} answers - String de respuestas separadas por comas (ej: "18,16,19,14,17" o "4,3,5,4,3")
 * @returns {Object} - Objeto con perfil e ID: { profile: 'Desarrollo', id: 1 }
 */
const getEmotionalProfile = (answers) => {
  // Convertir las respuestas a array de números
  const rawValues = answers.split(',').map((val) => parseInt(val.trim()));

  // Normalizar valores al rango 1-5
  const values = rawValues.map(normalizeValue);
  // Mapear a los ejes correspondientes
  const axes = {
    selfKnowledge: values[0], // autoconocimiento
    selfRegulation: values[1], // autorregulación
    motivation: values[2], // motivación
    empathy: values[3], // empatía
    socialSkills: values[4], // habilidades sociales
  };

  // Calcular la suma total de barras
  const totalBars = values.reduce((sum, val) => sum + val, 0);

  // Determinar el perfil según la lógica del documento
  const profileName = determineProfile(axes, totalBars);

  // Retornar objeto con perfil e ID
  const profileData = PROFILE_MAP[profileName];
  return {
    profile: profileData.name,
    id: profileData.id,
    rawValues, // Incluir valores originales para debugging
    normalizedValues: values, // Incluir valores normalizados
  };
};

/**
 * Función principal para determinar el perfil
 * @param {Object} axes - Objeto con los valores de cada eje
 * @param {number} totalBars - Suma total de barras
 * @returns {string} - El perfil emocional
 */
const determineProfile = (axes, totalBars) => {
  const { selfKnowledge, selfRegulation, motivation, empathy, socialSkills } =
    axes;

  // Perfiles básicos por rango de barras
  if (totalBars >= 5 && totalBars <= 10) {
    return 'Desarrollo';
  }

  if (totalBars >= 11 && totalBars <= 15) {
    return 'Equilibrado';
  }

  // Empático (16-17 barras)
  if (totalBars >= 16 && totalBars <= 17) {
    return 'Empatico';
  }

  // Caso especial para 18 barras
  if (totalBars === 18) {
    // Si empatía tiene 4 o más barras = Empático, sino Colaborativo
    return empathy >= 4 ? 'Empatico' : 'Colaborativo';
  }

  // Colaborativo - Autoconsciente - Autorregulado - Motivado (19-21 barras)
  if (totalBars >= 19 && totalBars <= 21) {
    return determineProfile19to21(axes);
  }

  // Autoconsciente - Autorregulado - Motivado - Estratégico (22-24 barras)
  if (totalBars >= 22 && totalBars <= 24) {
    if (totalBars === 22) {
      return determineProfile22(axes);
    }
    if (totalBars === 23) {
      return determineProfile23(axes);
    }
    if (totalBars === 24) {
      return determineProfile24(axes);
    }
  }

  // Líder Inspiracional (25 barras)
  if (totalBars === 25) {
    return 'Lider inspiracional';
  }

  // Por defecto, si no encaja en ningún rango
  return 'Sin clasificar';
};

/**
 * Determina el perfil para 19-21 barras
 */
const determineProfile19to21 = (axes) => {
  const { selfKnowledge, selfRegulation, motivation, empathy, socialSkills } =
    axes;
  const maxValue = Math.max(
    selfKnowledge,
    selfRegulation,
    motivation,
    empathy,
    socialSkills,
  );

  // Identificar qué ejes tienen el valor máximo
  const maxAxes = [];
  if (selfKnowledge === maxValue) maxAxes.push('selfKnowledge');
  if (selfRegulation === maxValue) maxAxes.push('selfRegulation');
  if (motivation === maxValue) maxAxes.push('motivation');
  if (empathy === maxValue) maxAxes.push('empathy');
  if (socialSkills === maxValue) maxAxes.push('socialSkills');

  // Casos de empate específicos
  if (
    maxAxes.includes('motivation') &&
    maxAxes.includes('selfRegulation') &&
    maxAxes.length === 2
  ) {
    return selfKnowledge >= 4 ? 'Motivado' : 'Autorregulado';
  }

  if (
    maxAxes.includes('selfKnowledge') &&
    maxAxes.includes('selfRegulation') &&
    maxAxes.length === 2
  ) {
    return motivation >= 4 ? 'Autoconsciente' : 'Autorregulado';
  }

  if (
    maxAxes.includes('empathy') &&
    maxAxes.includes('socialSkills') &&
    maxAxes.length === 2
  ) {
    return selfKnowledge >= 4 ? 'Autorregulado' : 'Colaborativo';
  }

  // Si empatía o habilidades sociales son los ejes con más barras = Colaborativo
  if (maxAxes.includes('empathy') || maxAxes.includes('socialSkills')) {
    return 'Colaborativo';
  }

  // Si autoconocimiento es el eje con más barras = Autoconsciente
  if (maxAxes.includes('selfKnowledge')) {
    return 'Autoconsciente';
  }

  // Si autorregulación es el eje con más barras = Autorregulado
  if (maxAxes.includes('selfRegulation')) {
    return 'Autorregulado';
  }

  // Si motivación es el eje con más barras = Motivado
  if (maxAxes.includes('motivation')) {
    return 'Motivado';
  }

  return 'Colaborativo'; // Por defecto
};

/**
 * Determina el perfil para 22 barras
 */
const determineProfile22 = (axes) => {
  const { selfKnowledge, selfRegulation, motivation, empathy, socialSkills } =
    axes;

  // Si autoconocimiento y motivación están en 5 barras = Estratégico
  if (selfKnowledge === 5 && motivation === 5) {
    return 'Estrategico';
  }

  // Si autoconocimiento y motivación están en 5 barras, verificar autorregulación
  if (selfKnowledge === 5 && motivation === 5) {
    return selfRegulation >= 4 ? 'Autorregulado' : 'Motivado';
  }

  // Si autoconocimiento y autorregulación están en 4 o 5 barras
  if (
    selfKnowledge >= 4 &&
    selfKnowledge <= 5 &&
    selfRegulation >= 4 &&
    selfRegulation <= 5
  ) {
    return motivation >= 4 ? 'Autoconsciente' : 'Autorregulado';
  }

  // Si motivación y autorregulación están en 4 o 5 barras
  if (
    motivation >= 4 &&
    motivation <= 5 &&
    selfRegulation >= 4 &&
    selfRegulation <= 5
  ) {
    return selfKnowledge >= 4 ? 'Motivado' : 'Autorregulado';
  }

  return 'Autorregulado'; // Por defecto
};

/**
 * Determina el perfil para 23 barras
 */
const determineProfile23 = (axes) => {
  const { selfKnowledge, selfRegulation, motivation, empathy, socialSkills } =
    axes;

  // Contar cuántos ejes tienen 5 barras
  const fiveBarAxes = [
    selfKnowledge,
    selfRegulation,
    motivation,
    empathy,
    socialSkills,
  ].filter((val) => val === 5).length;

  if (fiveBarAxes >= 3) {
    return socialSkills === 5 ? 'Lider inspiracional' : 'Estrategico';
  }

  // Casos específicos de dos ejes con 5 barras
  if (motivation === 5 && selfKnowledge === 5) return 'Estrategico';
  if (motivation === 5 && socialSkills === 5) return 'Lider inspiracional';
  if (motivation === 5 && selfRegulation === 5) return 'Motivado';
  if (empathy === 5 && selfRegulation === 5) return 'Estrategico';
  if (empathy === 5 && socialSkills === 5) return 'Lider inspiracional';
  if (selfKnowledge === 5 && socialSkills === 5) return 'Estrategico';
  if (socialSkills === 5 && selfRegulation === 5) return 'Estrategico';
  if (motivation === 5 && empathy === 5) return 'Motivado';
  if (selfKnowledge === 5 && empathy === 5) return 'Estrategico';
  if (selfRegulation === 5 && selfKnowledge === 5) return 'Estrategico';

  return 'Estrategico'; // Por defecto
};

/**
 * Determina el perfil para 24 barras
 */
const determineProfile24 = (axes) => {
  const { selfKnowledge, selfRegulation, motivation, empathy, socialSkills } =
    axes;

  // Verificar qué eje tiene 4 barras (el resto debería tener 5)
  if (motivation === 4) return 'Estrategico';
  if (empathy === 4) return 'Lider inspiracional';
  if (selfKnowledge === 4) return 'Lider inspiracional';
  if (selfRegulation === 4) return 'Lider inspiracional';
  if (socialSkills === 4) return 'Estrategico';

  return 'Estrategico'; // Por defecto
};

/**
 * Función auxiliar para obtener solo el nombre del perfil (para compatibilidad)
 * @param {string} answers - String de respuestas separadas por comas
 * @returns {string} - El nombre del perfil emocional
 */
const getEmotionalProfileName = (answers) => {
  const result = getEmotionalProfile(answers);
  return result.profile;
};

/**
 * Función auxiliar para obtener información detallada del perfil
 * @param {string} answers - String de respuestas separadas por comas
 * @returns {Object} - Objeto con perfil y detalles adicionales
 */
const getDetailedEmotionalProfile = (answers) => {
  const values = answers.split(',').map((val) => parseInt(val.trim()));
  const totalBars = values.reduce((sum, val) => sum + val, 0);
  const profileResult = getEmotionalProfile(answers);

  const axes = {
    selfKnowledge: values[0],
    selfRegulation: values[1],
    motivation: values[2],
    empathy: values[3],
    socialSkills: values[4],
  };

  return {
    profile: profileResult.profile,
    profileId: profileResult.id,
    totalBars,
    axes,
    axesNames: [
      'Autoconocimiento',
      'Autorregulación',
      'Motivación',
      'Empatía',
      'Habilidades sociales',
    ],
  };
};

module.exports = {
  getEmotionalProfile,
  getEmotionalProfileName,
  getDetailedEmotionalProfile,
  PROFILE_MAP,
};
